import Lottie from 'lottie-react'
import Image from 'next/image'
import React from 'react'

import { ContentfulRichTextContent } from '@/components/cms/__ContentfulRichTextContent'
import { MapPageContent } from '@/components/cms/__MapPageContent'
import { GridContainer } from '@/components/Grid'
import Layout from '@/components/Layout'
import { ResponsiveSpacer } from '@/components/Spacing'
import { BaseText, BodyText, H2Display } from '@/components/Typography'
import { PostsPageProps } from '@/interfaces/index'
import { fetchContent } from '@/utils/contentful/fetch-content'
import { ContentfulPostsQuery } from '@/utils/contentful/gql'
import { contentfulImageLoader } from '@/utils/helpers'

const PostsPage = ({ cmsData }: PostsPageProps) => {
  return (
    <Layout
      title={cmsData.pageTitle}
      url={`/thinking/${cmsData.slug}`}
      description={cmsData.pageDescription}
      imageCTA={cmsData.metaImageCta}
      useLightTheme={true}
    >
      <section className="bg-white text-almostBlack">
        <GridContainer>
          <div className="col-span-full">
            <H2Display>
              <h1>{cmsData.displayTitle}</h1>
            </H2Display>

            <ResponsiveSpacer size="xs" />

            <BaseText>
              <p className="italic max-w-[960px]">{cmsData.pageDescription}</p>

              <ResponsiveSpacer size="xs" />

              <div className="flex items-center gap-x-3">
                <div className="h-[46px] w-[46px] md:h-[56px] md:w-[56px] border-[4px] rounded-full border-yellow">
                  <Image
                    loader={() =>
                      contentfulImageLoader({
                        src: cmsData.authorHeadshot.url,
                        width: 320,
                        quality: 95,
                        fit: 'scale',
                      })
                    }
                    alt={cmsData.author}
                    src={cmsData.authorHeadshot.url}
                    layout="responsive"
                    height={cmsData.authorHeadshot.height}
                    width={cmsData.authorHeadshot.width}
                    className="rounded-full"
                  />
                </div>
                <p className="-mt-[6px] font-bold">{cmsData.author}</p>
              </div>
            </BaseText>
          </div>
        </GridContainer>

        <ResponsiveSpacer size="xs" />

        <GridContainer>
          <div className="col-span-full max-w-[960px]">
            {cmsData.animationJson && (
              <div>
                <Lottie animationData={cmsData.animationJson} />
              </div>
            )}

            {cmsData.bannerImage && (
              <Image
                loader={() =>
                  contentfulImageLoader({
                    src: cmsData.bannerImage.url,
                    width: 960,
                    quality: 95,
                    fit: 'scale',
                  })
                }
                alt={cmsData.pageTitle}
                src={cmsData.bannerImage.url}
                layout="responsive"
                height={cmsData.bannerImage.height}
                width={cmsData.bannerImage.width}
              />
            )}

            <ResponsiveSpacer size="md" />

            <BodyText>
              <div className="__shift-lab-rich-text-block">
                <ContentfulRichTextContent richText={cmsData.richText} />
              </div>
            </BodyText>
          </div>
        </GridContainer>

        <ResponsiveSpacer size="md" />
      </section>

      <MapPageContent pageContent={cmsData.pageContentCollection.items} />
    </Layout>
  )
}

export async function getStaticPaths(): Promise<any> {
  const data = await fetchContent(`
    {
     	postsCollection(limit: 2500) {
        items {
          slug
        }
      }
    }
  `)

  const paths = data?.postsCollection.items.map((slugFromCms: any) => {
    return {
      params: { slug: slugFromCms.slug },
    }
  })

  return {
    paths,
    fallback: false,
  }
}

export async function getStaticProps({ params }: any) {
  const data = await fetchContent(`{
    postsCollection(where: { slug: "${params.slug}" }, limit: 1) {
      items {
        ${ContentfulPostsQuery}
      }
    }
  }`)

  return {
    props: {
      cmsData: data.postsCollection.items[0],
    },
  }
}

export default PostsPage
